<template>
  <div class="bg-[#1C1D36] rounded-full p-1">
    <ComboBox
      ref="combobox"
      v-model="query"
      :suggestions="suggestions"
      :open="showSuggestions"
      class="relative w-full flex items-center"
      @selected="selectSuggestion">
      <form class="relative flex w-full" @submit.prevent="submit">
        <SearchCategoriesDropdown
          ref="searchCategoriesDropdown"
          v-model="category"
          @open="showSuggestions = false" />
        <div class="flex items-center ml-3 flex-grow">
          <ComboBoxTextbox
            class="text-primary-light-purple text-xs ml-1 bg-transparent w-full placeholder-current"
            :class="{ 'pr-16': !searching }"
            placeholder="Search"
            @click.native="trackSearchClickEvent"
            @focus.native="showSuggestions = true" />
          <SearchLoading v-if="searching" />
          <button
            v-if="query.length > 0"
            type="button"
            class="absolute right-0 mr-10 text-b60 text-xs"
            aria-label="Close"
            @click="query = ''">
            <i class="fa-regular fa-xmark"></i>
          </button>
          <button
            v-if="query.length > 0"
            class="absolute right-0 w-[28px] h-[28px] rounded-full bg-primary-offwhite text-primary-purple"
            aria-label="Submit"
            @click="submit">
            <i class="fa-regular fa-arrow-right"></i>
          </button>
        </div>
      </form>
      <ComboBoxSuggestions
        ref="suggestions"
        class="absolute z-[11] w-full mt-10 top-0 bg-p5 rounded-xl py-[10px] suggestion-box-shadow">
        <ComboBoxSuggestion
          slot-scope="{ suggestion, index, isFocused }"
          class="w-full px-5 py-3 text-left cursor-pointer hover:bg-white hover:bg-opacity-5 text-sm text-primary-offwhite"
          :class="{ 'bg-white bg-opacity-5': isFocused }"
          :suggestion="suggestion"
          :index="index"
          v-text="suggestion" />
      </ComboBoxSuggestions>
    </ComboBox>
  </div>
</template>

<script>
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';
import browseEventsData from '@/utils/events/browse-events-data';
import axios from 'axios';
import debounce from 'lodash/debounce';
import {
  ComboBox,
  ComboBoxTextbox,
  ComboBoxSuggestions,
  ComboBoxSuggestion,
} from 'vue-headless-combobox';
import { SEARCH_TYPES, SESSION_MAIN_SEARCH, SearchCategories } from '@/constants';
import SearchCategoriesDropdown from './search-categories-dropdown.vue';
import SearchLoading from './search-loading.vue';

export default {
  components: {
    ComboBox,
    ComboBoxTextbox,
    ComboBoxSuggestions,
    ComboBoxSuggestion,
    SearchCategoriesDropdown,
    SearchLoading,
  },

  data() {
    return {
      category: 'templates',
      query: '',
      showSuggestions: false,
      suggestions: [],
      skipFetch: false,
      searching: false,
    };
  },

  watch: {
    query(newVal) {
      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          $store: this.$store,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
        input_placeholder: 'search',
        search_category: this.category ? this.category : 'All Categories',
      };
      eventTracker(analyticsEvents.searchTermTyped, eventProperties);

      this.updateSearchSessionStorage('query', newVal);
      if (this.skipFetch) {
        return;
      }

      // Reset suggestions when query is empty or less than 2 characters.
      if (!newVal || !newVal.trim() || newVal.trim().length < 2) {
        this.suggestions = [];
        return;
      }

      this.showSuggestions = true;
      this.debounceGetSuggestions(newVal);
    },
    category(newVal) {
      this.updateSearchSessionStorage('category', newVal);
    },
  },

  mounted() {
    window.addEventListener('click', this.handleClickOutside);
    window.addEventListener('keyup', this.handleSearchKeypress);
    this.skipFetch = true;
    this.initSearchFromSessionStorage();
    this.$nextTick(() => {
      this.skipFetch = false;
    });
  },
  beforeDestroy() {
    window.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('keyup', this.handleSearchKeypress);
  },

  methods: {
    search() {
      if (this.searching || !this.query.trim()) {
        return;
      }
      this.searching = true;

      let route = `/browse/search/${this.query}/`;

      if (this.category) {
        route += `?category=${this.category}`;
      }

      // If user is searching under single category, then on submiting the search form, redirect
      // user directly to that category page
      const categorySlug = SearchCategories.find(item => item.id === this.category)?.slug;
      if (categorySlug) {
        route = `/browse/${categorySlug}/?q=${this.query}&search_header=1`;
      }

      // redirect to motionarray-front
      window.location.href = route;
    },

    getSuggestions(query) {
      const url = `/api/tags/search?tag-query=${query}&limit=5`;
      return axios.get(url).then(({ data }) => {
        if (data.length === 0) {
          eventTracker(analyticsEvents.searchAutoCompleteReturnedEmpty, {
            location: 'header',
            search_term: query,
            category: this.category.length === 0 ? 'all categories' : this.category,
          });
        }
        this.suggestions = data;
      });
    },

    debounceGetSuggestions: debounce(function (query) {
      // eslint-disable-line
      this.getSuggestions(query);
    }, 300),

    selectSuggestion(suggestion) {
      // Temporarily skip fetching new suggestions on input change.
      this.skipFetch = true;

      const typedQuery = this.query;
      this.suggestions = [];
      this.query = suggestion;

      // After the input is rendered, allow fetching new suggestions.
      this.$nextTick(() => {
        this.skipFetch = false;
      });

      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          $store: this.$store,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
        original_search_term: typedQuery,
      };
      eventTracker(analyticsEvents.suggestionSearchTermSelected, eventProperties);

      this.search();
    },

    submit() {
      this.$store.dispatch('search/setSearchParams', { type: SEARCH_TYPES.GLOBAL_SEARCH });

      this.suggestions = [];
      const eventProperties = {
        ...browseEventsData({
          $route: this.$route,
          $store: this.$store,
          location: 'header',
          categorySlug: this.$store.getters.categoryFilters.slug || '',
        }),
        search_term: this.query,
        location: 'header',
      };
      eventTracker(analyticsEvents.freeTextMediaSearched, eventProperties);
      this.search();
    },

    handleClickOutside(evt) {
      if (this.showSuggestions && !this.$refs.combobox.$el.contains(evt.target)) {
        this.showSuggestions = false;
      }
    },
    handleSearchKeypress(evt) {
      if (evt.key === 'Enter') {
        if (this.showSuggestions && this.query.length > 1) {
          this.showSuggestions = false;
        }
      }
    },
    initSearchFromSessionStorage() {
      if (typeof window !== 'undefined') {
        const mainSearchDataSession = this.getSearchFromSessionStorage();
        this.category = mainSearchDataSession?.category ?? this.category;
        this.query = mainSearchDataSession?.query ?? '';
      }
    },
    saveSearchToSessionStorage(mainSearchData) {
      sessionStorage.setItem(SESSION_MAIN_SEARCH, JSON.stringify(mainSearchData));
    },
    getSearchFromSessionStorage() {
      return JSON.parse(sessionStorage.getItem(SESSION_MAIN_SEARCH));
    },
    updateSearchSessionStorage(name, value) {
      const mainSearchDataSession = this.getSearchFromSessionStorage();
      const searchData = {
        ...mainSearchDataSession,
        [name]: value,
      };
      this.saveSearchToSessionStorage(searchData);
    },
    trackSearchClickEvent() {
      eventTracker(analyticsEvents.searchAreaClicked, {
        input_placeholder: 'search',
        location: 'header',
        search_category: this.category || 'All Categories',
      });
    },
  },
};
</script>

<style scoped>
.suggestion-box-shadow {
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.3);
}
.w-\[28px\] {
  width: 28px;
}
.h-\[28px\] {
  height: 28px;
}
.py-\[10px\] {
  padding-top: 10px;
  padding-bottom: 10px;
}
.z-\[11\] {
  z-index: 11;
}
.bg-\[\#1C1D36\] {
  background-color: #1C1D36;
}
</style>
